import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for debouncing function calls
 * Returns a debounced version of the callback that delays execution
 * until after wait milliseconds have elapsed since the last call;
 * You should memoize the callback with a useCallback to prevent
 * creating stale data and unnecessary re-renders.
 */
export function useDebounce<T extends (...args: any[]) => void>(callback: T, delay: number): T {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const callbackRef = useRef(callback);

    // Update callback ref when it changes
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    return useCallback(
        ((...args) => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            timerRef.current = setTimeout(() => {
                callbackRef.current(...args);
            }, delay);
        }) as T,
        [delay]
    );
}
